import type { AppStore } from '@/store/types'
import type { IResponseException } from '@/types'

export interface IHandledErrorLike {
  __handled?: boolean
  message?: string
  exceptionMessage?: string
}

export function markAsHandledError<T extends object>(error: T): T & { __handled: true } {
  (error as IHandledErrorLike).__handled = true
  return error as T & { __handled: true }
}

export function createHandledError(message: string): Error & { __handled: true } {
  return markAsHandledError(new Error(message))
}

export function isHandledError(error: unknown): error is IHandledErrorLike {
  return Boolean(error && typeof error === 'object' && (error as IHandledErrorLike).__handled === true)
}

export function isResponseException(error: unknown): error is IResponseException {
  return Boolean(
    error &&
    typeof error === 'object' &&
    typeof (error as IResponseException).exceptionMessage === 'string' &&
    Array.isArray((error as IResponseException).validationErrors),
  )
}

export function getErrorMessage(error: unknown): string {
  if (isResponseException(error)) return error.exceptionMessage
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object' && typeof (error as IHandledErrorLike).message === 'string') {
    return (error as IHandledErrorLike).message || 'Unexpected error'
  }
  return 'Unexpected error'
}

export function pushSnackbarMessage(store: AppStore | undefined, message: string): void {
  if (!store || !message) return

  void store.dispatch('Messages/add', {
    message,
    detailedMessage: '',
  })
}
